/**
 * StoreCard Component
 * 
 * Displays a single store card in the route overview list
 */

import { memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type StoreVisit } from '@/types/store-first';
import { MapPin, CheckCircle2, PlayCircle, PauseCircle, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StoreCardProps {
  visit: StoreVisit;
  onNavigate: () => void;
  onSignOff?: () => void;
  onStartVisit?: () => void;
  onMarkComplete?: () => void;
  completionData?: {
    storeId: number;
    completionPercentage: number;
    isComplete: boolean;
    postingComplete?: boolean;
    isSigned?: boolean;
    totalTasks: number;
    completedTasks: number;
  };
  disabled?: boolean;
  disabledReason?: string;
  isStartingVisit?: boolean;
  isMarkingComplete?: boolean;
}

export const StoreCard = memo(function StoreCard({ 
  visit, 
  onNavigate, 
  onSignOff, 
  onStartVisit,
  onMarkComplete,
  completionData,
  disabled = false,
  disabledReason,
  isStartingVisit = false,
  isMarkingComplete = false,
}: StoreCardProps) {
  const statusConfig = {
    not_started: {
      icon: MapPin,
      label: 'Not Started',
      color: 'bg-gray-100 text-gray-700',
      iconColor: 'text-gray-500'
    },
    pending: {
      icon: MapPin,
      label: 'Not Started',
      color: 'bg-gray-100 text-gray-700',
      iconColor: 'text-gray-500'
    },
    in_progress: {
      icon: PlayCircle,
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-700',
      iconColor: 'text-blue-500'
    },
    paused: {
      icon: PauseCircle,
      label: 'Paused',
      color: 'bg-orange-100 text-orange-700',
      iconColor: 'text-orange-500'
    },
    completed: {
      icon: CheckCircle2,
      label: 'Completed',
      color: 'bg-purple-100 text-purple-700',
      iconColor: 'text-purple-500'
    },
    signed_off: {
      icon: CheckCircle2,
      label: 'Signed Off',
      color: 'bg-green-100 text-green-700',
      iconColor: 'text-green-500'
    },
    reopened: {
      icon: PauseCircle,
      label: 'Reopened',
      color: 'bg-yellow-100 text-yellow-700',
      iconColor: 'text-yellow-500'
    },
    awaiting_signature: {
      icon: PenLine,
      label: 'Ready for Signature',
      color: 'bg-purple-100 text-purple-700',
      iconColor: 'text-purple-600'
    }
  };

  const config = statusConfig[visit.visitStatus];
  const StatusIcon = config.icon;

  // Each campaign has one packTask (pack completion)
  const totalTasks = visit.campaigns.length; // One task per campaign (pack completion)
  const completedTasks = visit.campaigns.filter(
    campaign => campaign.packTask?.status === 'completed' || campaign.packTask?.status === 'issue'
  ).length;

  // Use completion data if available, otherwise fall back to visit data
  const completionPercentage = completionData?.completionPercentage ?? 
    (totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0);
  const isComplete = completionData?.isComplete ?? (visit.visitStatus === 'completed');
  const displayTotalTasks = completionData?.totalTasks ?? totalTasks;
  const displayCompletedTasks = completionData?.completedTasks ?? completedTasks;
  
  // Check if store needs signature (all tasks done but not signed)
  const visitStatus = visit.visitStatus as string;
  const needsSignature =
    visitStatus !== 'signed_off' &&
    completionData?.postingComplete &&
    !completionData?.isSigned;

  // Determine badge status: prioritize completion data over visit status
  const badgeStatus: string =
    visitStatus === 'signed_off'
      ? 'signed_off'
      : needsSignature
        ? 'awaiting_signature'
        : isComplete
          ? 'completed'
          : visitStatus;
  const badgeConfig = statusConfig[badgeStatus as keyof typeof statusConfig];
  const BadgeIcon = badgeConfig.icon;

  // Proscriptive Workflow: Check if all tasks are complete
  const allTasksComplete = displayTotalTasks > 0 && displayCompletedTasks === displayTotalTasks;
  const showStartButton = visitStatus === 'not_started' || visitStatus === 'pending';
  const showContinueButton = visitStatus === 'in_progress';
  const showCompleteButton = visitStatus === 'in_progress' && allTasksComplete;
  const showSignatureButton = needsSignature;
  const showSignedBadge =
    visitStatus === 'signed_off' || (completionData?.isSigned && !needsSignature);

  return (
    <Card 
      className={cn(
        "mb-3 transition-all duration-200",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-md active:shadow-lg",
        isComplete && "opacity-90 border-green-300"
      )}
      onClick={disabled ? undefined : onNavigate}
      title={disabled ? disabledReason : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg mb-1 truncate">{visit.storeName}</h3>
            <div className="flex items-center gap-1 text-sm text-gray-600 mb-2 min-w-0">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="line-clamp-1 truncate">{visit.storeAddress}</span>
            </div>
          </div>
          
          <Badge className={cn(badgeConfig.color, "flex items-center gap-1 flex-shrink-0 whitespace-nowrap")}>
            <BadgeIcon className={cn("h-3 w-3", badgeConfig.iconColor)} />
            <span className="hidden sm:inline">{badgeConfig.label}</span>
            <span className="sm:hidden">
              {badgeStatus === 'awaiting_signature' && 'Ready Sig'}
              {badgeStatus === 'completed' && 'Done'}
              {badgeStatus === 'signed_off' && 'Signed'}
              {badgeStatus === 'in_progress' && 'In prog'}
              {(badgeStatus === 'not_started' || badgeStatus === 'pending') && 'Not started'}
              {badgeStatus === 'paused' && 'Paused'}
              {badgeStatus === 'reopened' && 'Reopened'}
            </span>
          </Badge>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 mt-3">
          <div className="flex items-center gap-4">
            <span>{visit.campaigns.length} {visit.campaigns.length === 1 ? 'campaign' : 'campaigns'}</span>
            <span>{totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}</span>
          </div>
        </div>

        {/* Progress indicator */}
        {displayTotalTasks > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Tasks completed</span>
              <span>{displayCompletedTasks} / {displayTotalTasks}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  isComplete ? "bg-green-600" : completionPercentage > 0 ? "bg-blue-600" : "bg-gray-300"
                )}
                style={{
                  width: `${completionPercentage}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Proscriptive Workflow: Explicit Action Buttons */}
        <div className="mt-3 space-y-2">
          {/* Start Visit Button */}
          {showStartButton && onStartVisit && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onStartVisit();
              }}
              disabled={disabled || isStartingVisit}
              className="w-full"
              size="sm"
            >
              {isStartingVisit ? (
                <>
                  <PlayCircle className="h-4 w-4 mr-2 animate-pulse" />
                  Starting Visit...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Start Visit
                </>
              )}
            </Button>
          )}

          {/* Continue Visit Button - Always enabled for in_progress stores */}
          {showContinueButton && !showCompleteButton && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onNavigate();
              }}
              disabled={disabled}
              className="w-full"
              size="sm"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Continue Visit
            </Button>
          )}

          {/* Mark Complete Button - Only when all tasks done */}
          {showCompleteButton && onMarkComplete && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onMarkComplete();
              }}
              disabled={disabled || isMarkingComplete}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              {isMarkingComplete ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2 animate-pulse" />
                  Marking Complete...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Store Complete
                </>
              )}
            </Button>
          )}

          {/* Get Signature Button */}
          {showSignatureButton && onSignOff && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onSignOff();
              }}
              disabled={disabled}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              size="sm"
            >
              <PenLine className="h-4 w-4 mr-2" />
              Capture Signature
            </Button>
          )}

          {/* Signed Off Badge - Read only */}
          {showSignedBadge && (
            <div className="w-full text-center py-2 bg-green-50 rounded-md border border-green-200">
              <div className="flex items-center justify-center gap-2 text-green-700 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                <span>Signed Off</span>
              </div>
            </div>
          )}

          {/* Disabled/Locked State Message */}
          {disabled && disabledReason && (
            <div className="text-xs text-gray-500 text-center py-1 bg-gray-50 rounded">
              {disabledReason}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

